/**
 * Dashboard Stats Component
 * Displays KPI cards with order statistics.
 * Role-aware: Order Desk sees intake-focused metrics; others see workflow metrics.
 * Includes stale-data preservation, retry with exponential backoff, and error banner.
 */

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { dedupedGet } from '../../lib/requestDedupe';
import { FileText, Clock, TrendingUp, Mail, AlertCircle, CheckCircle, ClipboardCheck, RefreshCw, CalendarDays, CalendarMinus, CalendarRange, UserX, RotateCcw } from 'lucide-react';
import OpsIntroOverlay from './OpsIntroOverlay';
import { KpiPillRow } from './KpiPillRow';

// OPS-INTRO-001 — smooth 0 -> value count-up on first load, and animated
// transitions on later changes (with a subtle pulse). Reduced-motion safe.
const CountUpValue = ({ value }) => {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  const [pulse, setPulse] = useState(false);
  const fromRef = useRef(null);
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const from = fromRef.current === null ? 0 : fromRef.current;
    fromRef.current = target;
    if (reduced || from === target) { setDisplay(target); return; }
    setPulse(true);
    const dur = 700;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => setPulse(false), 250);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <span className={`kpi-countup${pulse ? ' kpi-pulse' : ''}`}>{display}</span>;
};

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const MAX_RETRIES = 5;
// May 5, 2026 — bumped retry budget from 3×(1s,2s,4s)=7s to
// 5×(1s,2s,4s,4s,4s)=15s + capped per-attempt at 4s. Preview-env backend
// restarts (during deploys / hot-reload of large modules) take 5-10s and
// were exhausting the old 3-retry budget, surfacing 502s in the UI before
// the backend was ready. The dashboard now self-heals during typical
// restart windows without the user seeing the red "fetch failed" banner.
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 4000;

// Jul 2026 — DASH-SKELETON-001: during the very first fetch the cards used
// to render "0" (falsy default) which read as wrong data while the payload
// was in flight. A pulsing skeleton bar now fills the value slot until the
// first successful response; later background refreshes keep showing the
// last known values (unchanged behavior).
const KPIValueSkeleton = () => (
  <span
    className="inline-block h-6 w-10 rounded-md bg-slate-200 animate-pulse align-middle"
    data-testid="kpi-value-skeleton"
    aria-hidden="true"
  />
);

const KPICard = ({ icon: Icon, label, value, stale, loading, ariaLabel, accentColor }) => (
  <div 
    className={`kpi-card${stale ? ' opacity-60' : ''}`}
    role="group"
    tabIndex={0}
    aria-label={ariaLabel || (loading ? `${label}: loading` : `${label}: ${value}`)}
    data-testid={`kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="kpi-card-accent" style={accentColor ? { background: accentColor } : undefined} />
    <div className="kpi-card-icon">
      <Icon className="h-4 w-4" aria-hidden="true" />
    </div>
    <div className="kpi-card-body">
      <div className="kpi-card-label">{label}</div>
      <div className="kpi-card-value">{loading ? <KPIValueSkeleton /> : <CountUpValue value={value} />}</div>
    </div>
  </div>
);

/**
 * Product Breakdown Cards — Order Desk only
 * Compact KPI-style cards showing today's product distribution.
 * Only non-zero products are rendered. Matches KPI card design language.
 */
const PRODUCT_CARDS = [
  { key: 'tax_search', label: 'Tax Search', color: '#3B82F6' },
  { key: 'mls', label: 'MLS', color: '#8B5CF6' },
  { key: 'update_tax', label: 'Update Tax', color: '#F59E0B' },
  { key: 'current_owner_search', label: 'Current Owner Search', color: '#10B981' },
  { key: 'avr_search', label: 'AVR Search', color: '#EC4899' },
  { key: 'other', label: 'Other', color: '#6B7280' },
];

// Jun 2026 — code-level variant chips (option c): shows product variants
// like "Recon - FM001-RE" with counts so exception variants are no longer
// merged into a single product bucket. Data: stats.product_code_breakdown.
const ProductVariantChips = ({ variants }) => {
  if (!variants || variants.length === 0) return null;
  const MAX_CHIPS = 12;
  const shown = variants.slice(0, MAX_CHIPS);
  return (
    <div className="mt-4" data-testid="product-variant-breakdown">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
        By Product Code
      </p>
      <div className="flex flex-wrap gap-2">
        {shown.map((v, i) => (
          <span
            key={`${v.display}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 shadow-sm"
            data-testid={`variant-chip-${i}`}
            title={v.code ? `Product: ${v.product} · Code: ${v.code}` : `Product: ${v.product}`}
          >
            <span className="font-medium">{v.display}</span>
            <span className="font-bold text-slate-900">{v.count}</span>
          </span>
        ))}
        {variants.length > MAX_CHIPS && (
          <span className="text-xs text-slate-400 self-center">
            +{variants.length - MAX_CHIPS} more
          </span>
        )}
      </div>
    </div>
  );
};

const ProductBreakdownCards = ({ breakdown, codeBreakdown, stale, loading }) => {
  if (loading) {
    return (
      <div className="mb-6" data-testid="product-breakdown-section">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Product Breakdown Today</h3>
        <div className="flex flex-wrap gap-4" data-testid="product-breakdown-skeleton">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-2xl bg-slate-100 animate-pulse"
              style={{ width: '240px', height: '104px' }}
            />
          ))}
        </div>
      </div>
    );
  }
  if (!breakdown || breakdown.total === 0) {
    return (
      <div className="mb-6" data-testid="product-breakdown-section">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Product Breakdown Today</h3>
        <p className="text-sm text-slate-400">No orders entered today yet.</p>
      </div>
    );
  }

  // Only render cards with non-zero counts
  const activeCards = PRODUCT_CARDS.filter(card => {
    const data = breakdown[card.key];
    return data && data.count > 0;
  });

  if (activeCards.length === 0) {
    return (
      <div className="mb-6" data-testid="product-breakdown-section">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Product Breakdown Today</h3>
        <p className="text-sm text-slate-400">No orders entered today yet.</p>
      </div>
    );
  }

  return (
    <div className={`mb-6${stale ? ' opacity-60' : ''}`} data-testid="product-breakdown-section">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
        Product Breakdown Today
        <span className="ml-2 text-xs font-normal text-slate-400">({breakdown.total} total)</span>
      </h3>
      <div className="flex flex-wrap gap-4" style={{ justifyContent: activeCards.length <= 2 ? 'center' : 'flex-start' }}>
        {activeCards.map(card => {
          const data = breakdown[card.key];
          return (
            <div
              key={card.key}
              className="relative overflow-hidden bg-white transition-all duration-200 cursor-default hover:shadow-[0_10px_24px_rgba(17,24,39,0.12)]"
              style={{
                borderRadius: '16px',
                boxShadow: '0 6px 18px rgba(17,24,39,0.08)',
                width: '240px',
                minWidth: '200px',
                flexShrink: 0,
              }}
              data-testid={`breakdown-card-${card.key}`}
            >
              {/* Accent bar — matches KPI card gradient bar */}
              <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${card.color}, ${card.color}dd)` }} />
              <div className="px-4 pt-4 pb-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: card.color }} />
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{card.label}</span>
                </div>
                <div className="text-[32px] font-bold text-slate-900 leading-none mt-1">{data.count}</div>
                <div className="text-xs text-slate-400 mt-1.5">{data.percentage}% of today</div>
              </div>
            </div>
          );
        })}
      </div>
      <ProductVariantChips variants={codeBreakdown} />
    </div>
  );
};

const DashboardStats = forwardRef(({ orderTypeFilter, unassignedOnly, year, user }, ref) => {
  const [stats, setStats] = useState({});
  // DASH-SKELETON-001: true until the first payload (or terminal failure)
  // arrives, so cards show skeletons instead of misleading zeros.
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const staleRef = useRef({});
  const retryTimerRef = useRef(null);
  // MLS-TAX-SPLIT (Aug 23, 2026): sequence guard — a superseded in-flight
  // request (e.g. 'all' fired before the URL's 'tax' propagated) must never
  // overwrite the latest scoped payload.
  const reqSeqRef = useRef(0);

  // OD-SIMPLIFY-001: the Order Desk presentation no longer mounts this component;
  // the role check is kept for the legacy payload shape only.
  const isOrderDesk = user?.role === 'order_desk';

  const fetchWithRetry = useCallback(async (orderType, unassigned, yearVal, attempt = 0, reqId = null) => {
    if (reqId === null) reqId = reqSeqRef.current;
    try {
      const params = {};
      if (user?.role === 'admin' && orderType && orderType !== 'all') {
        params.order_type = orderType;
      }
      if (unassigned) {
        params.unassigned_only = 'true';
      }
      // May 5, 2026 — forward year filter so stats cards match the table.
      if (yearVal && yearVal !== 'all') {
        params.year = yearVal;
      }
      // MLS-UNIFIED-STATUS-001: Tax TL "MLS ORDERS" view → MLS-scoped KPI payload
      // (same shared predicate as the tabs/grid). TAX ORDERS view unchanged.
      if (user?.role === 'tax_search_team_leader'
          && new URLSearchParams(window.location.search).get('view') === 'mls') {
        params.workflow_view = 'mls';
      }

      const response = await dedupedGet(`${API}/dashboard/stats`, { params });
      if (reqId !== reqSeqRef.current) return false; // superseded request
      // DEGRADED-STALE-001 (Jul 2026): the backend marks Atlas-timeout
      // responses with degraded:true. Stale-snapshot payloads carry real
      // numbers — display them WITH the banner; zeroed payloads are treated
      // as a failed attempt so retry/backoff engages and zeros never render.
      if (response.data?.degraded) {
        if (response.data.degraded_reason === 'atlas-timeout-stale') {
          const clean = { ...response.data };
          delete clean.degraded;
          delete clean.degraded_reason;
          setStats(clean);
          staleRef.current = clean;
          setInitialLoading(false);
          setFetchError(true);
          setIsRetrying(false);
          retryTimerRef.current = setTimeout(() => fetchWithRetry(orderType, unassigned, yearVal, 0, reqId), 30000);
          return false;
        }
        // Zeroed degraded payload: surface the banner IMMEDIATELY (honest
        // feedback from the first response), then let retry/backoff run.
        setFetchError(true);
        throw new Error('degraded zero payload');
      }
      setStats(response.data);
      staleRef.current = response.data;
      // OPS-INTRO-001: cache REAL metrics for the Performance Moment theme
      // (never fabricated — theme is skipped when this cache is absent).
      try {
        if (typeof response.data.total_orders === 'number') {
          const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id || null;
          localStorage.setItem('ops-stats-cache', JSON.stringify({
            total_orders: response.data.total_orders,
            open_orders: response.data.open_orders || 0,
            user_id: uid,
            at: new Date().toISOString()
          }));
        }
      } catch { /* storage unavailable */ }
      setInitialLoading(false);
      setFetchError(false);
      setIsRetrying(false);
      return true;
    } catch (err) {
      if (reqId !== reqSeqRef.current) return false; // superseded request
      console.error(`Dashboard stats fetch failed (attempt ${attempt + 1}/${MAX_RETRIES}):`, err?.message);
      if (attempt < MAX_RETRIES - 1) {
        const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
        setIsRetrying(true);
        await new Promise(r => { retryTimerRef.current = setTimeout(r, delay); });
        return fetchWithRetry(orderType, unassigned, yearVal, attempt + 1, reqId);
      }
      if (Object.keys(staleRef.current).length > 0) {
        setStats(staleRef.current);
        setInitialLoading(false);
      }
      // No stale data (first load): keep the skeletons pulsing — never
      // render misleading zeros as if they were real.
      setFetchError(true);
      setIsRetrying(false);
      // AUTO-RECOVER: retry on its own in 30s; no user action required.
      retryTimerRef.current = setTimeout(() => fetchWithRetry(orderType, unassigned, yearVal, 0, reqId), 30000);
      return false;
    }
  }, [user?.role]);

  const fetchStats = useCallback(async (orderType = orderTypeFilter, unassigned = unassignedOnly, yearVal = year) => {
    setFetchError(false);
    setIsRetrying(false);
    clearTimeout(retryTimerRef.current);
    reqSeqRef.current += 1;
    await fetchWithRetry(orderType, unassigned, yearVal, 0, reqSeqRef.current);
  }, [orderTypeFilter, unassignedOnly, year, fetchWithRetry]);

  useImperativeHandle(ref, () => ({
    refreshStats: fetchStats
  }));
  
  useEffect(() => {
    fetchStats();
    return () => clearTimeout(retryTimerRef.current);
  }, [orderTypeFilter, unassignedOnly, year, user?.role]);

  // =========================================================================
  // ORDER DESK: Intake-focused cards
  // =========================================================================
  if (isOrderDesk) {
    const orderDeskCards = [
      { icon: CalendarDays, label: 'Entered Today', value: stats.entered_today || 0, key: 'entered_today', accentColor: '#3B82F6' },
      { icon: CalendarMinus, label: 'Entered Yesterday', value: stats.entered_yesterday || 0, key: 'entered_yesterday', accentColor: '#8B5CF6' },
      { icon: CalendarRange, label: 'This Week', value: stats.entered_this_week || 0, key: 'entered_this_week', accentColor: '#10B981' },
      { icon: UserX, label: 'Unassigned', value: stats.unassigned_orders || 0, key: 'unassigned', accentColor: '#EF4444' },
    ];

    return (
      <div data-testid="dashboard-stats">
        <OpsIntroOverlay />
        {fetchError && (
          <div
            data-testid="dashboard-stats-error-banner"
            className="flex items-center justify-between bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-2.5 mb-4 text-sm"
          >
            <span>
              Live stats are temporarily unavailable
              {Object.keys(staleRef.current).length > 0
                ? ' — showing last known values. Retrying automatically.'
                : ' — retrying automatically.'}
            </span>
            <button
              data-testid="dashboard-stats-retry-btn"
              onClick={() => fetchStats()}
              disabled={isRetrying}
              className="ml-3 inline-flex items-center gap-1.5 font-medium text-amber-900 hover:text-amber-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5${isRetrying ? ' animate-spin' : ''}`} />
              Retry
            </button>
          </div>
        )}
        <KpiPillRow items={orderDeskCards} loading={initialLoading} stale={fetchError}
          renderValue={(v) => <CountUpValue value={v} />} className="mb-3" testId="dashboard-kpi-pill-row" />
        <ProductBreakdownCards breakdown={stats.product_breakdown} codeBreakdown={stats.product_code_breakdown} stale={fetchError} loading={initialLoading} />
      </div>
    );
  }

  // =========================================================================
  // ALL OTHER ROLES: Workflow-focused cards (original)
  // =========================================================================
  
  // Search roles should not see Mail-Away cards (Search products don't use Mail-Away workflow)
  const SEARCH_ROLES = ['search_processor', 'title_search_team_leader'];
  const isSearchContext = SEARCH_ROLES.includes(user?.role) || orderTypeFilter === 'search';

  const allStatsConfig = [
    { icon: FileText, label: 'Total Orders', value: stats.total_orders || stats.total_assigned || 0, key: 'total' },
    { icon: Clock, label: 'Open Orders', value: stats.open_orders || stats.pending_assignment || 0, key: 'open' },
    { icon: TrendingUp, label: 'Pending', value: stats.in_progress || 0, key: 'pending' },
    { icon: Mail, label: 'Mail-Away', value: stats.mail_away || 0, key: 'mailaway' },
    { icon: ClipboardCheck, label: 'QC Review', value: stats.qc_review || 0, key: 'qc_review' },
    { icon: Mail, label: 'Mail-Away Received', value: stats.mail_away_received || 0, key: 'mailaway_received' },
    { icon: AlertCircle, label: 'Clarification', value: stats.clarification || 0, key: 'clarification' },
    { icon: CheckCircle, label: 'Clarification Received', value: stats.clarification_received || 0, key: 'clarification_received' },
    // Feb 2026 — QC Workflow Phase 1: dedicated KPI on Admin/TL/Processor
    // dashboards so soft-returned orders are visible at a glance. DB status
    // remains `pending_correction`; UI label is "Correction Required".
    { icon: RotateCcw, label: 'Correction Required', value: stats.returned_for_correction || 0, key: 'returned_for_correction' },
  ];

  const statsConfig = stats.mls_buckets
    // MLS-UNIFIED-STATUS-001: exclusive unified buckets — Total = Σ buckets.
    ? [
      { icon: FileText, label: 'Total Orders', value: stats.total_orders || 0, key: 'total' },
      { icon: Clock, label: 'Open', value: stats.mls_buckets.open || 0, key: 'open' },
      // MLS-DASHBOARD-ASSIGNMENT-UI-001: top "In Progress" KPI card removed (status filter / counts elsewhere unchanged)
      { icon: Clock, label: 'Pending', value: stats.mls_buckets.pending || 0, key: 'pending' },
      { icon: Mail, label: 'Mail-Away', value: stats.mls_buckets.mail_away || 0, key: 'mailaway' },
      { icon: Mail, label: 'Mail-Away Received', value: stats.mls_buckets.mail_away_received || 0, key: 'mailaway_received' },
      { icon: AlertCircle, label: 'Clarification', value: stats.mls_buckets.clarification || 0, key: 'clarification' },
      { icon: CheckCircle, label: 'Clarification Received', value: stats.mls_buckets.clarification_received || 0, key: 'clarification_received' },
      { icon: ClipboardCheck, label: 'QC Review', value: stats.mls_buckets.qc_review || 0, key: 'qc_review' },
      { icon: RotateCcw, label: 'Correction Required', value: stats.mls_buckets.pending_correction || 0, key: 'returned_for_correction' },
    ]
    : isSearchContext
    ? allStatsConfig.filter(s => !['mailaway', 'mailaway_received'].includes(s.key))
    : allStatsConfig;

  return (
    <div data-testid="dashboard-stats">
      <OpsIntroOverlay />
      {fetchError && (
        <div
          data-testid="dashboard-stats-error-banner"
          className="flex items-center justify-between bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-2.5 mb-4 text-sm"
        >
          <span>
            Live stats are temporarily unavailable
            {Object.keys(staleRef.current).length > 0
              ? ' — showing last known values. Retrying automatically.'
              : ' — retrying automatically.'}
          </span>
          <button
            data-testid="dashboard-stats-retry-btn"
            onClick={() => fetchStats()}
            disabled={isRetrying}
            className="ml-3 inline-flex items-center gap-1.5 font-medium text-amber-900 hover:text-amber-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5${isRetrying ? ' animate-spin' : ''}`} />
            Retry
          </button>
        </div>
      )}
      {/* KPI-PILL-001: single-row pill (Admin / Tax TL / Title TL Operations dashboards) */}
      <KpiPillRow items={statsConfig} loading={initialLoading} stale={fetchError}
        renderValue={(v) => <CountUpValue value={v} />} className="mb-3" testId="dashboard-kpi-pill-row" />
    </div>
  );
});

DashboardStats.displayName = 'DashboardStats';

export { DashboardStats, KPICard };
export default DashboardStats;
